import type React from "react";
import { fmtHours, fmtInt } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { DonutWithLegend, type DonutDatum } from "../visuals/DonutWithLegend";
import { TopBars, type TopBarItem } from "../visuals/TopBars";
import {
  ACCENT_ORANGE,
  TIER_COLORS,
  TIER_LABELS,
  extendedRateAccent,
  fleetIdleRateAccent,
  type TierKey,
} from "../colors";

const TIER_ORDER: TierKey[] = ["high", "watch_list", "on_track", "excellent"];

function OverviewSlideView({ data, resetKey }: SlideContext): React.JSX.Element {
  const { kpis, stores, priority_tiers: tiers } = data.methodology;

  const extColor = extendedRateAccent(kpis.extended_session_rate);
  const idleColor = fleetIdleRateAccent(kpis.fleet_idle_rate_pct);

  const tierData: DonutDatum[] = TIER_ORDER
    .map((k) => ({
      name: TIER_LABELS[k],
      value: tiers[k].length,
      color: TIER_COLORS[k],
    }))
    .filter((d) => d.value > 0);
  const tierTotal = tierData.reduce((sum, d) => sum + d.value, 0);

  const adoptionData: DonutDatum[] = [
    { name: "Active", value: kpis.active_stores, color: ACCENT_ORANGE },
    {
      name: "Inactive",
      value: Math.max(0, kpis.total_stores - kpis.active_stores),
      color: "rgba(255,255,255,0.08)",
    },
  ];

  const topStores: TopBarItem[] = [...stores]
    .sort((a, b) => b.productive_hrs_per_device - a.productive_hrs_per_device)
    .slice(0, 5)
    .map((s) => ({
      label: s.store,
      value: s.productive_hrs_per_device,
      display: `${s.productive_hrs_per_device.toFixed(1)}h`,
    }));

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Tile
          label="Total Sessions"
          value={kpis.total_sessions}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          resetKey={resetKey}
        />
        <Tile
          label="Productive Hours"
          value={kpis.productive_hours}
          format={fmtHours}
          delay={200}
          sub="8h credit applied"
          resetKey={resetKey}
        />
        <Tile
          label="Extended Rate"
          value={kpis.extended_session_rate}
          format={(v) => `${v.toFixed(1)}%`}
          delay={300}
          accent={extColor}
          resetKey={resetKey}
        />
        <Tile
          label="Fleet Idle Rate"
          value={kpis.fleet_idle_rate_pct}
          format={(v) => `${v.toFixed(1)}%`}
          delay={400}
          accent={idleColor}
          resetKey={resetKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <DonutWithLegend
          title="Team Health Distribution"
          data={tierData}
          centerValue={tierTotal}
          centerLabel="teams"
          delay={500}
        />
        <DonutWithLegend
          title="Network Adoption"
          data={adoptionData}
          centerValue={`${Math.round(kpis.network_adoption_pct)}%`}
          centerLabel="adoption"
          delay={600}
          accentCenter={ACCENT_ORANGE}
        />
      </div>

      <TopBars title="Top Teams — Productive Hrs / Device" items={topStores} delay={700} />
    </>
  );
}

export const overviewSlide: Slide = {
  id: "overview",
  title: "Overview",
  render: (ctx) => <OverviewSlideView {...ctx} />,
};
